export type Locale = "en" | "he";

export const dictionaries = {
  en: {
    metaTitle: "MyAPI — WhatsApp connection management",
    metaDescription: "SaaS for managing WhatsApp devices via an unofficial API",
    brand: "MyAPI",
    login: "Log in",
    register: "Sign up",
    logout: "Log out",
    alreadyHaveAccount: "Already have an account?",
    noAccount: "Don't have an account?",
    email: "Email",
    password: "Password",
    passwordHint: "At least 8 characters, including letters and numbers",
    loginTitle: "Log in",
    loginSubtitle: "Sign in to your MyAPI account",
    registerTitle: "Sign up",
    registerSubtitle: "Create an account to manage WhatsApp devices",
    createAccount: "Create account",
    submitting: "Please wait…",
    redirecting: "Opening dashboard…",
    networkError: "Network error. Try again.",
    heroTitle: "WhatsApp connections — simple, stable, ready to scale",
    heroBody:
      "Link devices, control sessions, and keep connections persistent — a solid base for a commercial WhatsApp API service.",
    ctaStart: "Get started",
    ctaLogin: "I already have an account",
    featureDevicesTitle: "Multiple devices",
    featureDevicesBody:
      "Manage several WhatsApp connections from one account, with full session isolation.",
    featurePairTitle: "QR or pairing code",
    featurePairBody:
      "Link a new device by scanning a QR code or using a phone pairing code.",
    featureControlTitle: "Connection control",
    featureControlBody:
      "Power devices on and off without unlinking WhatsApp, or fully log out when needed.",
    featureApiTitle: "API-ready",
    featureApiBody:
      "Clean foundation for API keys, messaging, and webhooks later.",
    myDevices: "My devices",
    addDevice: "Add device",
    unpairedBlock:
      "Connect your existing device to WhatsApp before adding another one.",
    emptyTitle: "No devices yet",
    emptyBody:
      "Create your first device, link it to WhatsApp with QR or a code, and manage connection state here.",
    addFirstDevice: "Add your first device",
    colDevice: "Device",
    colUid: "API UID",
    colCreated: "Created",
    colConnection: "WhatsApp connection",
    colPower: "Device power",
    colActions: "Actions",
    copyUid: "Copy UID",
    uidCopied: "UID copied",
    userUidLabel: "Account UID",
    rotateUid: "Rotate UID",
    cancel: "Cancel",
    uidRotateUserTitle: "Rotate account UID",
    uidRotateDeviceTitle: "Rotate device UID",
    uidRotateWarningLead:
      "You are about to replace this public API identifier with a new one.",
    uidRotateWarningBody:
      "Any integration, script, webhook, or third-party system that still calls the old UID will stop working immediately. You must update every stored reference yourself after rotation.",
    uidRotateWarningResponsibility:
      "You are solely responsible for replacing the old UID with the new one in all of your systems. MyAPI cannot migrate or notify external consumers for you.",
    uidRotateCurrentLabel: "Current UID",
    uidRotateConfirmLabel: "Type or paste the current UID to confirm",
    uidRotateConfirmPlaceholder: "Paste the current UID exactly",
    uidRotateConfirm: "Rotate UID",
    uidRotateMismatch: "The value does not match the current UID",
    uidRotated: "UID rotated successfully",
    apiDocsLink: "API docs",
    apiDocsTitle: "API reference",
    apiDocsIntro:
      "Call device commands over HTTP. Paths use your account UID and device UID: /api/{userUid}/{deviceUid}.",
    apiBaseTitle: "Endpoint",
    apiBaseBody:
      "Send a POST request to /api/{userUid}/{deviceUid} with a JSON body. GET /api shows this documentation.",
    apiUidHint:
      "Both UIDs are shown in the dashboard. Treat them as secrets — anyone who has them can call commands on that device.",
    apiCommandsTitle: "Commands",
    apiExample: "Example",
    powerOn: "On",
    powerOff: "Off",
    turnOn: "Turn on",
    turnOff: "Turn off",
    connectQr: "Connect with QR",
    unlinkWhatsApp: "Disconnect from WhatsApp",
    delete: "Delete",
    deviceAdded: "Device added",
    deviceDeleted: "Device deleted",
    unlinkedOk: "Device unlinked from WhatsApp",
    createFailed: "Failed to create device",
    powerFailed: "Failed to update power state",
    unlinkFailed: "Failed to disconnect from WhatsApp",
    deleteFailed: "Failed to delete",
    unlinkConfirm:
      "This will unlink the device from the WhatsApp account. Continue?",
    deleteConfirm: "Delete {name}?",
    statusUnpaired: "Not linked",
    statusConnecting: "Connecting…",
    statusConnected: "Connected",
    statusDisconnected: "Temporarily disconnected",
    statusError: "Error",
    pairTitle: "Connect to WhatsApp",
    pairDesc: "{name} — scan the code or use a pairing code.",
    useCodeInstead: "Connect with a code instead",
    backToQr: "Back to QR code",
    phoneLabel: "Phone number (international)",
    phoneHint: "No + and no leading 0. Example: 972501234567",
    getCode: "Get pairing code",
    gettingCode: "Requesting code…",
    codeForWhatsApp: "Code to enter in WhatsApp",
    codeWhere:
      "WhatsApp → Linked devices → Link with phone number",
    qrExpired: "QR code expired",
    tryAgain: "Try again",
    close: "Close",
    pairStartFailed: "Failed to start pairing",
    language: "Language",
    theme: "Theme",
    themeLight: "Light",
    themeDark: "Dark",
    langEn: "English",
    langHe: "עברית",
    deviceName: "Device {n}",
    errEmailTaken: "This email is already registered",
    errInvalidCreds: "Invalid email or password",
    errGeneric: "Something went wrong",
    errRateLimit: "Too many attempts. Try again later.",
    errUnpairedExists:
      "Connect your existing device to WhatsApp before adding another one.",
    navDevices: "Devices",
    dashboardSubtitle:
      "Link WhatsApp numbers, control their sessions, and grab API credentials.",
    statTotal: "Total devices",
    statConnected: "Connected",
    statRunning: "Powered on",
    statUnlinked: "Not linked",
    apiCredsTitle: "API credentials",
    apiCredsBody:
      "Combine your account UID with a device UID to call commands. Keep both secret.",
    endpointLabel: "Endpoint",
    deviceUidLabel: "Device UID",
    createdOn: "Created {date}",
    notLinkedYet: "No number linked yet",
    confirm: "Confirm",
    deleteTitle: "Delete device",
    deleteBody: "{name} and its WhatsApp session will be permanently removed.",
    unlinkTitle: "Disconnect from WhatsApp",
    deleteLinkedTitle: "Disconnect from WhatsApp first",
    deleteLinkedBody:
      "{name} is still linked to WhatsApp. Disconnect it from WhatsApp, then you can delete the device.",
    understood: "Got it",
    signedInAs: "Signed in as",
    devicesHeading: "Devices",
    turnOnToLink: "Turn the device on to link it",
  },
  he: {
    metaTitle: "MyAPI — ניהול חיבורי WhatsApp",
    metaDescription: "שירות SaaS לניהול מכשירי WhatsApp באמצעות API לא רשמי",
    brand: "MyAPI",
    login: "התחברות",
    register: "הרשמה",
    logout: "התנתקות",
    alreadyHaveAccount: "כבר רשומים?",
    noAccount: "אין לכם חשבון?",
    email: "אימייל",
    password: "סיסמה",
    passwordHint: "לפחות 8 תווים, כולל אותיות ומספרים",
    loginTitle: "התחברות",
    loginSubtitle: "היכנסו לחשבון MyAPI שלכם",
    registerTitle: "הרשמה",
    registerSubtitle: "צרו חשבון חדש כדי לנהל מכשירי WhatsApp",
    createAccount: "יצירת חשבון",
    submitting: "רגע…",
    redirecting: "מעביר לדשבורד…",
    networkError: "שגיאת רשת. נסו שוב.",
    heroTitle: "ניהול חיבורי WhatsApp — פשוט, יציב ומוכן לצמיחה",
    heroBody:
      "חברו מכשירים, שלטו בסשנים, ושמרו על חיבור מתמשך — בסיס מקצועי לשירות WhatsApp API מסחרי.",
    ctaStart: "התחילו עכשיו",
    ctaLogin: "כבר יש לי חשבון",
    featureDevicesTitle: "מספר מכשירים",
    featureDevicesBody:
      "נהלו כמה חיבורי WhatsApp מחשבון אחד, עם בידוד מלא בין סשנים.",
    featurePairTitle: "QR או קוד התחברות",
    featurePairBody:
      "חברו מכשיר חדש בסריקת QR או באמצעות קוד pairing מהטלפון.",
    featureControlTitle: "שליטה במצב החיבור",
    featureControlBody:
      "הפעלה וכיבוי בלי לנתק מחשבון WhatsApp, ו־Logout מלא כשצריך.",
    featureApiTitle: "מוכן ל־API",
    featureApiBody:
      "תשתית נקייה להוספת מפתחות API, שליחת הודעות ו־Webhooks בהמשך.",
    myDevices: "המכשירים שלי",
    addDevice: "הוסף מכשיר",
    unpairedBlock:
      "יש לחבר את המכשיר הקיים ל־WhatsApp לפני הוספת מכשיר נוסף.",
    emptyTitle: "עדיין אין מכשירים",
    emptyBody:
      "צרו מכשיר ראשון, חברו אותו ל־WhatsApp באמצעות QR או קוד, ונהלו את מצב החיבור מכאן.",
    addFirstDevice: "הוסיפו את המכשיר הראשון",
    colDevice: "מכשיר",
    colUid: "API UID",
    colCreated: "תאריך יצירה",
    colConnection: "חיבור WhatsApp",
    colPower: "מצב מכשיר",
    colActions: "פעולות",
    copyUid: "העתק UID",
    uidCopied: "ה־UID הועתק",
    userUidLabel: "UID של החשבון",
    rotateUid: "רענון UID",
    cancel: "ביטול",
    uidRotateUserTitle: "רענון UID של החשבון",
    uidRotateDeviceTitle: "רענון UID של המכשיר",
    uidRotateWarningLead: "אתם עומדים להחליף את מזהה ה־API הציבורי במזהה חדש.",
    uidRotateWarningBody:
      "כל אינטגרציה, סקריפט, webhook או מערכת חיצונית שתמשיך לקרוא ל־UID הישן תפסיק לעבוד מיד. עליכם לעדכן בעצמכם את כל המקומות שבהם ה־UID הישן שמור.",
    uidRotateWarningResponsibility:
      "אתם נושאים באחריות הבלעדית להחלפת ה־UID הישן בחדש בכל המערכות שלכם. MyAPI אינו מעביר או מעדכן צרכנים חיצוניים עבורכם.",
    uidRotateCurrentLabel: "ה־UID הנוכחי",
    uidRotateConfirmLabel: "העתיקו או הקלידו את ה־UID הנוכחי לאימות",
    uidRotateConfirmPlaceholder: "הדביקו כאן את ה־UID הנוכחי בדיוק",
    uidRotateConfirm: "רענון UID",
    uidRotateMismatch: "הערך שהוזן אינו תואם ל־UID הנוכחי",
    uidRotated: "ה־UID עודכן בהצלחה",
    apiDocsLink: "תיעוד API",
    apiDocsTitle: "תיעוד API",
    apiDocsIntro:
      "שלחו פקודות למכשירים דרך HTTP. הנתיב משתמש ב־UID של החשבון ושל המכשיר: /api/{userUid}/{deviceUid}.",
    apiBaseTitle: "נקודת קצה",
    apiBaseBody:
      "שלחו בקשת POST אל /api/{userUid}/{deviceUid} עם גוף JSON. GET /api מציג את התיעוד הזה.",
    apiUidHint:
      "שני ה־UID מוצגים בדשבורד. התייחסו אליהם כסודות — מי שיש לו אותם יכול להריץ פקודות על המכשיר.",
    apiCommandsTitle: "פקודות",
    apiExample: "דוגמה",
    powerOn: "מופעל",
    powerOff: "כבוי",
    turnOn: "הפעלה",
    turnOff: "כיבוי",
    connectQr: "חיבור באמצעות QR",
    unlinkWhatsApp: "התנתק מ־WhatsApp",
    delete: "מחיקה",
    deviceAdded: "המכשיר נוסף",
    deviceDeleted: "המכשיר נמחק",
    unlinkedOk: "המכשיר נותק מ־WhatsApp",
    createFailed: "יצירת מכשיר נכשלה",
    powerFailed: "עדכון מצב נכשל",
    unlinkFailed: "ניתוק מ־WhatsApp נכשל",
    deleteFailed: "מחיקה נכשלה",
    unlinkConfirm:
      "פעולה זו תבטל את קישור המכשיר לחשבון WhatsApp. להמשיך?",
    deleteConfirm: "למחוק את {name}?",
    statusUnpaired: "לא מקושר",
    statusConnecting: "מתחבר…",
    statusConnected: "מחובר",
    statusDisconnected: "מנותק זמנית",
    statusError: "שגיאה",
    pairTitle: "חיבור ל־WhatsApp",
    pairDesc: "{name} — סרקו את הקוד או השתמשו בקוד התחברות.",
    useCodeInstead: "התחבר באמצעות קוד במקום זאת",
    backToQr: "חזרה ל־QR Code",
    phoneLabel: "מספר טלפון (בינלאומי)",
    phoneHint: "ללא + וללא 0 בהתחלה. לדוגמה: 972501234567",
    getCode: "קבל קוד התחברות",
    gettingCode: "מבקש קוד…",
    codeForWhatsApp: "הקוד להזנה ב־WhatsApp",
    codeWhere: "WhatsApp → מכשירים מקושרים → קישור עם מספר טלפון",
    qrExpired: "פג תוקף קוד ה־QR",
    tryAgain: "נסו שוב",
    close: "סגור",
    pairStartFailed: "נכשל להתחיל חיבור",
    language: "שפה",
    theme: "מצב תצוגה",
    themeLight: "בהיר",
    themeDark: "כהה",
    langEn: "English",
    langHe: "עברית",
    deviceName: "מכשיר {n}",
    errEmailTaken: "כתובת האימייל כבר רשומה במערכת",
    errInvalidCreds: "אימייל או סיסמה שגויים",
    errGeneric: "אירעה שגיאה",
    errRateLimit: "יותר מדי ניסיונות. נסו שוב מאוחר יותר.",
    errUnpairedExists:
      "יש לחבר את המכשיר הקיים ל־WhatsApp לפני הוספת מכשיר נוסף.",
    navDevices: "מכשירים",
    dashboardSubtitle: "חברו מספרי WhatsApp, שלטו בסשנים וקבלו פרטי גישה ל־API.",
    statTotal: "סה״כ מכשירים",
    statConnected: "מחוברים",
    statRunning: "מופעלים",
    statUnlinked: "לא מקושרים",
    apiCredsTitle: "פרטי גישה ל־API",
    apiCredsBody:
      "שלבו את ה־UID של החשבון עם UID של מכשיר כדי להריץ פקודות. שמרו על שניהם בסוד.",
    endpointLabel: "נקודת קצה",
    deviceUidLabel: "UID של המכשיר",
    createdOn: "נוצר {date}",
    notLinkedYet: "עדיין לא קושר מספר",
    confirm: "אישור",
    deleteTitle: "מחיקת מכשיר",
    deleteBody: "{name} וסשן ה־WhatsApp שלו יימחקו לצמיתות.",
    unlinkTitle: "ניתוק מ־WhatsApp",
    deleteLinkedTitle: "יש לנתק קודם מ־WhatsApp",
    deleteLinkedBody:
      "{name} עדיין מקושר ל־WhatsApp. נתקו אותו מ־WhatsApp ורק אז תוכלו למחוק את המכשיר.",
    understood: "הבנתי",
    signedInAs: "מחובר/ת בתור",
    devicesHeading: "מכשירים",
    turnOnToLink: "הפעילו את המכשיר כדי לחבר אותו",
  },
} as const;

export type DictKey = keyof typeof dictionaries.en;

export function t(
  locale: Locale,
  key: DictKey,
  vars?: Record<string, string | number>,
) {
  let s: string = dictionaries[locale][key] || dictionaries.en[key];
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      s = s.replaceAll(`{${k}}`, String(v));
    }
  }
  return s;
}
